

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Route, Square, Navigation } from 'lucide-react';
import { GeoAutomationPOI } from '../services/customerApi';
import { calculateRelativePosition, formatDistance, getPOIColor } from '../utils/poiUtils';
import PathNetworkMiniMap from './PathNetworkMiniMap';

interface POISidePanelProps {
  pois: GeoAutomationPOI[]; // Nearby/filtered POIs
  allPois?: GeoAutomationPOI[]; // ALL POIs in scene
  onNavigate: (poi: GeoAutomationPOI) => void;
  onClose: () => void;
  currentPosition?: { latitude: number; longitude: number; altitude: number; heading?: number };
  selectedPOI?: GeoAutomationPOI | null;
  pathNetwork?: number[][][];
}

const POISidePanel: React.FC<POISidePanelProps> = ({
  pois,
  allPois = [],
  onNavigate,
  onClose,
  currentPosition,
  selectedPOI,
  pathNetwork
}) => {

  console.log('📍 [PANEL DEBUG] POISidePanel render');
  console.log('📍 [PANEL DEBUG] currentPosition:', currentPosition);
  console.log('📍 [PANEL DEBUG] pois count:', pois.length);
  console.log('📍 [PANEL DEBUG] allPois count:', allPois.length);
  console.log('📍 [PANEL DEBUG] pathNetwork count:', pathNetwork?.length);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredPOI, setHoveredPOI] = useState<string | null>(null);

   // Memoize mapped POI data
  const mappedAllPois = React.useMemo(() =>
    allPois.map(poi => ({
      id: poi.id,
      latitude: poi.latitude,
      longitude: poi.longitude,
      name: poi.name,
      layerName: poi.layerName,
    })),
    [allPois]
  );

  const mappedNearbyPois = React.useMemo(() =>
    pois.map(poi => ({
      latitude: poi.latitude,
      longitude: poi.longitude,
      name: poi.name
    })),
    [pois]
  );

  const mappedHighlightedPOI = React.useMemo(() =>
    selectedPOI ? {
      latitude: selectedPOI.latitude,
      longitude: selectedPOI.longitude
    } : undefined,
    [selectedPOI]
  );

  // Calculate relative positions for radar (pois already filtered by parent)
  const poisWithPosition = React.useMemo(() => {
    if (!currentPosition) return [];

    const heading = currentPosition.heading || 0;

    return pois.map(poi => {
      const relative = calculateRelativePosition(
        currentPosition.latitude,
        currentPosition.longitude,
        heading,
        poi.latitude,
        poi.longitude
      );

      return {
        ...poi,
        distance: relative.distance,
        direction: relative.direction,
        relativeBearing: relative.relativeBearing
      };
    });
  }, [currentPosition, pois]);

  const getPOIIcon = (type: string) => {
    switch (type) {
      case 'POINT':
        return MapPin;
      case 'LINESTRING':
        return Route;
      case 'POLYGON':
        return Square;
      default:
        return MapPin;
    }
  };

  if (!currentPosition) return null;

  return (
    <div 
      className={`fixed right-0 top-0 bottom-0 z-40 bg-[#161513]/60 backdrop-blur-sm transition-all duration-300 ease-in-out shadow-lg border-l border-[#0CB4E4]/30 flex flex-col ${
        isCollapsed ? 'w-12' : 'w-80'
      }`}
    >
      {/* Collapse/Expand Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)} 
        className="absolute top-[40px] left-[-16px] w-8 h-8 bg-[#161513] rounded-full border border-[#0CB4E4]/30 flex items-center justify-center shadow-md hover:bg-[#0CB4E4]/20 transition-colors"
        aria-label={isCollapsed ? "Expand panel" : "Collapse panel"} 
      >
        {isCollapsed ? (
          <ChevronLeft className="w-5 h-5 text-gray-300" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-300" />
        )}
      </button>

      {!isCollapsed && (
        <div className="flex flex-col h-full w-full">
          {/* Header */}
          <div className="px-4 py-4 border-b border-white/10">
            <h3 className="font-medium text-lg text-white tracking-wider">
              Nearby Points
            </h3>
            <p className="text-xs text-gray-400 mt-1">
              {pois.length} POI{pois.length !== 1 ? 's' : ''} within 100m
            </p>
          </div>

          {/* Compact Radar */}
          <div className="px-4 py-3 border-b border-white/10 flex justify-center">
            <svg width="120" height="120">
              {/* Background circle */}
              <circle
                cx="60"
                cy="60"
                r="55"
                fill="rgba(8, 121, 163, 0.1)"
                stroke="rgba(8, 121, 163, 0.3)"
                strokeWidth="1"
              />
              
              {/* Distance rings */}
              {[0.33, 0.66, 1].map((fraction, i) => (
                <circle
                  key={i}
                  cx="60"
                  cy="60"
                  r={55 * fraction}
                  fill="none"
                  stroke="rgba(255, 255, 255, 0.1)"
                  strokeWidth="1"
                  strokeDasharray={i === 2 ? undefined : "2,2"}
                />
              ))}

              {/* Forward indicator */}
              <polygon
                points="60,5 55,10 65,10"
                fill="rgba(8, 121, 163, 0.8)"
              />

              {/* POI dots */}
              {poisWithPosition.slice(0, 8).map(poi => {
                const bearing = poi.relativeBearing;
                const angleRad = (bearing * Math.PI) / 180;
                const scale = Math.min(poi.distance / 100, 1); // Max 100m
                const x = 60 + Math.sin(angleRad) * 55 * scale;
                const y = 60 - Math.cos(angleRad) * 55 * scale;
                const color = getPOIColor(poi.type);

                return (
                  <circle
                    key={poi.id}
                    cx={x}
                    cy={y}
                    r="4"
                    fill={color}
                    stroke="white"
                    strokeWidth="1.5"
                    style={{
                      cursor: 'pointer',
                      opacity: hoveredPOI === poi.id ? 1 : 0.8
                    }}
                    onMouseEnter={() => setHoveredPOI(poi.id)}
                    onMouseLeave={() => setHoveredPOI(null)}
                  />
                );
              })}

              {/* User position */}
              <circle cx="60" cy="60" r="5" fill="#0CB4E4" stroke="white" strokeWidth="2" />
            </svg>
          </div>

          {/* POI List */}
          <div className="flex-1 overflow-y-auto px-4 py-2">
            {pois.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No POIs nearby
              </div>
            ) : (
              <div className="space-y-2">
                {poisWithPosition.map(poi => {
                  const Icon = getPOIIcon(poi.type);
                  const color = getPOIColor(poi.type);
                  const isHovered = hoveredPOI === poi.id;

                  return (
                    <div
                      key={poi.id}
                      className="relative"
                      onMouseEnter={() => setHoveredPOI(poi.id)}
                      onMouseLeave={() => setHoveredPOI(null)}
                    >
                      {/* Compact POI Item */}
                      <div
                        className={`p-3 rounded-lg border transition-all cursor-pointer ${
                          isHovered
                            ? 'bg-[#0CB4E4]/20 border-[#0CB4E4]'
                            : 'bg-[#161513]/40 border-[#0CB4E4]/30 hover:bg-[#0CB4E4]/10'
                        }`}
                        onClick={() => onNavigate(poi)}
                      >
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: color }}
                          >
                            <Icon size={16} color="white" />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                              {poi.name}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {formatDistance(poi.distance)} {poi.direction}
                            </div>
                          </div>

                          {/* Navigate arrow */}
                          {isHovered && (
                            <Navigation
                              size={16}
                              className="text-[#0CB4E4] flex-shrink-0"
                            />
                          )}
                        </div>
                      </div>

                      {/* Simple tooltip on hover */}
                      {isHovered && (
                        <div className="absolute left-0 top-full mt-2 w-full p-2 bg-[#161513] border border-[#0CB4E4] rounded text-xs z-50">
                          <div className="text-white/70">
                            {poi.latitude.toFixed(5)}, {poi.longitude.toFixed(5)} • {poi.altitude.toFixed(1)}m
                          </div>
                          {poi.layerName && (
                            <div className="text-white/50 mt-1">Layer: {poi.layerName}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Path Network Mini Map */}
          {pathNetwork && pathNetwork.length > 0 && (
            <PathNetworkMiniMap
              key="path-network-map"  // Prevent unmounting
              pathNetwork={pathNetwork}
              currentPosition={currentPosition ? {
                latitude: currentPosition.latitude,
                longitude: currentPosition.longitude
              } : undefined}
              allPois={mappedAllPois}          // ← Use memoized
              
            />
          )}

          {/* Footer Info */}
          <div className="px-4 py-3 border-t border-white/10 bg-[#161513]/40">
            <div className="text-xs text-gray-400 text-center">
              Hover over POI for details
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POISidePanel;